/**
 * Shared NavBar component — identical to the homepage nav
 * Dark Tech / Glassmorphism design
 * Colors: #0c0c0c bg, #57d7e2 cyan accent
 * Font: Barlow
 * Mobile: hamburger menu with slide-down drawer
 * Note: framer-motion removed from critical path — uses CSS transitions instead
 */

import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";
import { POSTS, CATEGORIES } from "@/lib/blogMeta.generated";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";
import { INDEXED_WORDMARK } from "@/lib/brand";
import CoverImage from "@/components/CoverImage";
import { VIDEOS } from "@/lib/videos";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

const SEO_SERVICES = [
  { label: "SEO Audit & Strategy", desc: "Get direction for growth.", href: "/seo-audit-and-strategy" },
  { label: "AI SEO", desc: "Stay visible in LLMs.", href: "/ai-seo" },
  { label: "Technical SEO", desc: "Strengthen site health.", href: "/technical-seo" },
  { label: "Local SEO", desc: "Win local visibility.", href: "/local-seo" },
  { label: "Content Production", desc: "Publish content that ranks.", href: "/content-production" },
  { label: "Programmatic SEO", desc: "Capture long-tail demand at scale.", href: "/programmatic-seo" },
  { label: "Link Building", desc: "Increase authority with backlinks.", href: "/link-building" },
];

const WEB_SERVICES = [
  { label: "Web Design", desc: "High-performance websites designed to convert.", href: "/web-design" },
  { label: "Web Development", desc: "Fast, scalable, SEO-ready builds.", href: "/web-development" },
];

// Latest published blog post for the mega menu
const latestPost = [...POSTS].sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())[0];
const latestPostCategory = latestPost ? CATEGORIES.find(c => latestPost.categories.includes(c.slug)) : undefined;

const ArrowUpRight = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none" style={{ display: "inline-block" }}>
    <path d="M3 11L11 3M11 3H5M11 3v6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function ServiceItem({ label, desc, href, onClose }: { label: string; desc: string; href: string; onClose?: () => void }) {
  const [hov, setHov] = useState(false);
  return (
    <Link
      href={href}
      onClick={() => onClose?.()}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "flex-start", gap: "10px",
        padding: "9px 12px", borderRadius: "8px", textDecoration: "none",
        background: hov ? "rgba(87,215,226,0.07)" : "transparent",
        transition: "background 0.15s",
      }}
    >
      <div>
        <div style={{ color: "rgba(255,255,255,0.9)", fontSize: "14px", fontWeight: 500, fontFamily: ff }}>{label}</div>
        <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "12px", fontFamily: ff, marginTop: "2px" }}>{desc}</div>
      </div>
    </Link>
  );
}

const RESOURCES_SECTIONS = [
  {
    label: "Tools",
    href: "/tools",
    items: [
      { label: "Domain Rating Checker", desc: "Check any site's Ahrefs DR — free.", href: "/tools/domain-rating-checker" },
      { label: "LLM Visibility Checker", desc: "Your brand's visibility in LLMs.", href: "/tools/llm-visibility-checker" },
    ],
  },
  {
    label: "Research",
    href: "/research",
    items: [
      { label: "Studies", desc: "Original SEO research and analysis.", href: "/studies" },
      { label: "Data", desc: "Raw benchmarks and keyword datasets.", href: "/data" },
    ],
  },
  {
    label: "Learn",
    href: "/learn",
    items: [
      { label: "Blog", desc: "SEO, content, and growth articles.", href: "/blog" },
      { label: "Glossary", desc: "SEO and marketing terms defined.", href: "/glossary" },
      ...(VIDEOS.length > 0 ? [{ label: "Videos", desc: "The team on SEO and AI search.", href: "/videos" }] : []),
    ],
  },
];

export default function NavBar() {
  const [scrolled, setScrolled] = useState(false);
  const [pastFold, setPastFold] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resOpenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resourcesDropdownRef = useRef<HTMLDivElement>(null);

  // The nav is position:fixed and overlays page content, so any page element that
  // needs to sit flush below it must know its height. Rather than have each page
  // hardcode a magic number (and break when the strip/row height changes), NavBar
  // measures its own height and publishes it as the --site-nav-height CSS
  // variable for pages to consume.
  const desktopRowRef = useRef<HTMLDivElement>(null);
  const mobileRowRef = useRef<HTMLDivElement>(null);

  // Blogger Outreach sells its own product: the nav points at the order builder
  // instead of the video review.
  const [pathname] = useLocation();
  const onBloggerOutreach = pathname.startsWith("/blogger-outreach");
  const ctaHref = onBloggerOutreach ? "/blogger-outreach#order" : STRATEGIST_URL;
  const ctaLabel = onBloggerOutreach ? "Order now →" : "Get my free video review →";
  // Already on the page: scroll to the builder ourselves. `body{overflow-x:hidden}`
  // stops Chrome doing it natively for same-document #anchor links.
  const onCtaClick = (e: React.MouseEvent) => {
    if (!onBloggerOutreach) return;
    const target = document.getElementById("order");
    if (!target) return; // let the browser navigate normally
    e.preventDefault();
    history.replaceState(null, "", "#order");
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Blogger Outreach pins its own 72px offer bar above the nav, so the whole nav
  // sits that much lower and every fixed child has to be pushed down with it.
  const navTop = onBloggerOutreach ? 72 : 0;
  // Dropdowns are fixed-positioned below the nav row.
  const dropdownTop = 57 + navTop;
  // Past the fold on Blogger Outreach the nav gets out of the way — the offer bar
  // stays pinned and the page is a single buying flow from there down.
  const navHidden = onBloggerOutreach && pastFold && !mobileOpen;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!onBloggerOutreach) { setPastFold(false); return; }
    const onScroll = () => setPastFold(window.scrollY > window.innerHeight * 0.75);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onBloggerOutreach]);

  // Publish the nav's full height to --site-nav-height (see refs above).
  useEffect(() => {
    const measure = () => {
      // offsetHeight of these elements is unaffected by the strip's scroll-collapse
      // (which only clips via max-height) or by the mobile drawer (a separate node).
      // The hidden nav row (desktop vs mobile) reports 0, so the sum is strip + row.
      const desktop = desktopRowRef.current?.offsetHeight ?? 0;
      const mobile = mobileRowRef.current?.offsetHeight ?? 0;
      const full = desktop + mobile;
      if (full > 0) {
        document.documentElement.style.setProperty("--site-nav-height", `${full}px`);
      }
    };

    // Measure now, after the next paint, and once web fonts have loaded — the Barlow
    // swap grows the nav row, so a single mount-time read is stale on the final layout.
    measure();
    const raf = requestAnimationFrame(measure);
    if (typeof document !== "undefined" && document.fonts?.ready) {
      document.fonts.ready.then(measure).catch(() => {});
    }

    // Keep it correct across viewport changes / late reflow. The ResizeObserver fires
    // whenever a measured row's box changes (font swap, breakpoint, zoom).
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    [desktopRowRef.current, mobileRowRef.current].forEach(
      (el) => el && ro?.observe(el),
    );
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Close dropdowns on Escape key
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setServicesOpen(false);
        setResourcesOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Close resources dropdown on outside click
  useEffect(() => {
    if (!resourcesOpen) return;
    const onClick = (e: MouseEvent) => {
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(e.target as Node)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [resourcesOpen]);

  const openServices = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    setResourcesOpen(false);
    openTimerRef.current = setTimeout(() => setServicesOpen(true), 150);
  };
  const closeServicesNow = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (timerRef.current) clearTimeout(timerRef.current);
    setServicesOpen(false);
  };
  const closeServices = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    timerRef.current = setTimeout(() => setServicesOpen(false), 150);
  };

  const openResources = () => {
    if (resTimerRef.current) clearTimeout(resTimerRef.current);
    if (resOpenTimerRef.current) clearTimeout(resOpenTimerRef.current);
    setServicesOpen(false);
    resOpenTimerRef.current = setTimeout(() => setResourcesOpen(true), 150);
  };
  const closeResourcesNow = () => {
    if (resOpenTimerRef.current) clearTimeout(resOpenTimerRef.current);
    if (resTimerRef.current) clearTimeout(resTimerRef.current);
    setResourcesOpen(false);
  };
  const closeResources = () => {
    if (resOpenTimerRef.current) clearTimeout(resOpenTimerRef.current);
    resTimerRef.current = setTimeout(() => setResourcesOpen(false), 150);
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileServicesOpen(false);
    setMobileResourcesOpen(false);
  };

  return (
    <>
      {/* ── DESKTOP BACKDROP OVERLAY ── */}
      <div
        onClick={() => { closeServices(); closeResourcesNow(); }}
        style={{
          position: "fixed", inset: 0, zIndex: 90,
          backdropFilter: (servicesOpen || resourcesOpen) ? "blur(6px)" : "none",
          WebkitBackdropFilter: (servicesOpen || resourcesOpen) ? "blur(6px)" : "none",
          background: (servicesOpen || resourcesOpen) ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0)",
          opacity: (servicesOpen || resourcesOpen) ? 1 : 0,
          visibility: (servicesOpen || resourcesOpen) ? "visible" : "hidden",
          pointerEvents: (servicesOpen || resourcesOpen) ? "auto" : "none",
          transition: "opacity 0.22s ease-out",
          cursor: "default",
        }}
      />

      {/* ── NAVIGATION ── */}
      <nav style={{
        position: "fixed", top: navTop, left: 0, right: 0, zIndex: 100,
        backdropFilter: "none",
        backgroundColor: scrolled || mobileOpen ? "#0a0c0c" : "transparent",
        borderBottom: "none",
        transform: navHidden ? "translateY(-100%)" : "translateY(0)",
        opacity: navHidden ? 0 : 1,
        pointerEvents: navHidden ? "none" : "auto",
        transition: "background-color 0.3s, transform 0.3s ease, opacity 0.3s ease",
      }}>
        {/* ── DESKTOP NAV ── */}
        <div ref={desktopRowRef} style={{
          display: "grid", gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center", padding: "18px 72px",
          maxWidth: "1440px", margin: "0 auto", position: "relative",
        }} className="nav-desktop">
          {/* Logo */}
          <div>
            <Link href="/" style={{ display: "inline-block" }}>
              <img src={INDEXED_WORDMARK} alt="Indexed" width="130" height="21" style={{ width: "130px", height: "21px", objectFit: "contain" }} />
            </Link>
          </div>

          {/* Nav items */}
          <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
            {/* Services with dropdown */}
            <div onMouseEnter={openServices} onMouseLeave={closeServices} style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0px" }}>
                <Link href="/services" style={{
                  display: "flex", alignItems: "center",
                  padding: "8px 4px 8px 16px", background: "none", border: "none", cursor: "pointer",
                  color: servicesOpen ? "#57d7e2" : "rgba(255,255,255,0.85)",
                  fontSize: "15px", fontWeight: 500, fontFamily: ff,
                  transition: "color 0.15s", textDecoration: "none",
                }}>
                  Services
                </Link>
                <button type="button" aria-label="Toggle services menu" aria-expanded={servicesOpen} style={{
                  display: "flex", alignItems: "center",
                  padding: "8px 16px 8px 4px", background: "none", border: "none", cursor: "pointer",
                  color: servicesOpen ? "#57d7e2" : "rgba(255,255,255,0.85)",
                  transition: "color 0.15s",
                }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
                    style={{ transform: servicesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              {/* Invisible hover bridge */}
              {servicesOpen && (
                <div onMouseEnter={openServices} style={{
                  position: "fixed", top: `${dropdownTop}px`,
                  left: "calc(50vw - 430px)", width: "860px", height: "12px",
                  background: "transparent", zIndex: 199,
                }} />
              )}

              {/* Mega-menu dropdown — CSS transition instead of framer-motion */}
              <div
                onMouseEnter={openServices}
                onMouseLeave={closeServices}
                style={{
                  position: "fixed", top: `${dropdownTop + 1}px`,
                  left: "calc(50vw - 430px)", width: "860px",
                  paddingTop: "10px",
                  background: "rgba(8,14,16,0.97)",
                  backdropFilter: "blur(28px)",
                  border: "1px solid rgba(87,215,226,0.13)",
                  borderRadius: "20px", overflow: "hidden",
                  boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
                  zIndex: 200,
                  opacity: servicesOpen ? 1 : 0,
                  transform: servicesOpen ? "translateY(0)" : "translateY(-8px)",
                  visibility: servicesOpen ? "visible" : "hidden",
                  pointerEvents: servicesOpen ? "auto" : "none",
                  transition: "opacity 0.18s ease-out, transform 0.18s ease-out",
                }}
              >
                <div style={{ display: "flex" }}>
                  {/* Left: SEO + Web services */}
                  <div style={{ flex: 1, padding: "32px 36px 0" }}>
                    <Link href="/seo-agency"
                      style={{ display: "flex", alignItems: "center", gap: "8px", textDecoration: "none", marginBottom: "8px" }}>
                      <span style={{ color: "#57d7e2", fontSize: "17px", fontWeight: 700, fontFamily: ff }}>SEO</span>
                      <ArrowUpRight />
                    </Link>
                    <p style={{ color: "rgba(255,255,255,0.45)", fontSize: "14px", fontFamily: ff, margin: "0 0 14px" }}>
                      Full-stack SEO systems that drive visibility, authority, and growth.
                    </p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px", marginBottom: "20px" }}>
                      {SEO_SERVICES.map((s) => (
                        <ServiceItem key={s.label} label={s.label} desc={s.desc} href={s.href} onClose={closeServicesNow} />
                      ))}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", paddingBottom: "28px" }}>
                      {WEB_SERVICES.map((s) => (
                        <Link key={s.label} href={s.href} onClick={closeServicesNow}
                          style={{
                            display: "block", padding: "16px 18px", borderRadius: "12px",
                            border: "1px solid rgba(87,215,226,0.1)", textDecoration: "none",
                            background: "rgba(87,215,226,0.03)",
                          }}>
                          <div style={{ color: "#57d7e2", fontSize: "14px", fontWeight: 700, fontFamily: ff, marginBottom: "6px", display: "flex", alignItems: "center", gap: "5px" }}>
                            {s.label} <ArrowUpRight />
                          </div>
                          <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "14px", fontFamily: ff, lineHeight: 1.5 }}>{s.desc}</div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Right: Latest blog post */}
                  <div style={{ width: "240px", flexShrink: 0, padding: "32px 28px", borderLeft: "1px solid rgba(255,255,255,0.06)" }}>
                    <div style={{ color: "#57d7e2", fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>
                      From the blog
                    </div>
                    {latestPost && (
                      <Link href={`/blog/${latestPost.slug}`} onClick={closeServicesNow} style={{ textDecoration: "none", display: "block", marginBottom: "20px" }}>
                        <div style={{ borderRadius: "10px", overflow: "hidden", marginBottom: "12px", aspectRatio: "16/9" }}>
                          <CoverImage post={latestPost} imgStyle={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                        </div>
                        {latestPostCategory && (
                          <span style={{ color: latestPostCategory.color, fontSize: "11px", fontWeight: 700, fontFamily: ff, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            {latestPostCategory.name}
                          </span>
                        )}
                        <p style={{ color: "white", fontSize: "14px", fontWeight: 600, fontFamily: ff, lineHeight: 1.4, margin: "4px 0 0", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {latestPost.title}
                        </p>
                      </Link>
                    )}
                    <Link href="/blog" onClick={closeServicesNow}
                      style={{ color: "#57d7e2", fontSize: "13px", fontWeight: 600, fontFamily: ff, textDecoration: "none", display: "flex", alignItems: "center", gap: "5px" }}>
                      All articles <ArrowUpRight />
                    </Link>
                  </div>
                </div>

                {/* Trust strip */}
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                  padding: "16px 36px",
                }}>
                  {[
                    "Top 1% SEO expertise, trusted by enterprises across the US, UK & EU.",
                    "Your full SEO team on demand, without management overhead.",
                    "New here? You're eligible for a free session with our lead strategist.",
                  ].map((t, i) => (
                    <div key={i} style={{
                      color: "rgba(255,255,255,0.38)", fontSize: "11.5px", fontFamily: ff, lineHeight: 1.5,
                      paddingLeft: i > 0 ? "20px" : 0,
                      borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.06)" : "none",
                    }}>{t}</div>
                  ))}
                </div>
              </div>
            </div>

            <Link href="/method" style={{ padding: "8px 16px", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.85)", fontSize: "15px", fontWeight: 500, fontFamily: ff, textDecoration: "none" }}>Method</Link>
            <Link href="/client-stories" style={{ padding: "8px 16px", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.85)", fontSize: "15px", fontWeight: 500, fontFamily: ff, textDecoration: "none" }}>Work</Link>

            {/* Resources with dropdown */}
            <div ref={resourcesDropdownRef} onMouseEnter={openResources} onMouseLeave={closeResources} style={{ position: "relative" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0px" }}>
                <button type="button" style={{
                  display: "flex", alignItems: "center",
                  padding: "8px 4px 8px 16px", background: "none", border: "none", cursor: "pointer",
                  color: resourcesOpen ? "#57d7e2" : "rgba(255,255,255,0.85)",
                  fontSize: "15px", fontWeight: 500, fontFamily: ff,
                  transition: "color 0.15s",
                }}>
                  Resources
                </button>
                <button type="button" aria-label="Toggle resources menu" aria-expanded={resourcesOpen} style={{
                  display: "flex", alignItems: "center",
                  padding: "8px 16px 8px 4px", background: "none", border: "none", cursor: "pointer",
                  color: resourcesOpen ? "#57d7e2" : "rgba(255,255,255,0.85)",
                  transition: "color 0.15s",
                }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true"
                    style={{ transform: resourcesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}>
                    <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              {/* Invisible hover bridge */}
              {resourcesOpen && (
                <div onMouseEnter={openResources} style={{
                  position: "fixed", top: `${dropdownTop}px`,
                  left: "calc(50vw - 470px)", width: "940px", height: "12px",
                  background: "transparent", zIndex: 199,
                }} />
              )}

              {/* Resources mega-menu dropdown — 3 columns + featured post */}
              <div
                onMouseEnter={openResources}
                onMouseLeave={closeResources}
                style={{
                  position: "fixed", top: `${dropdownTop + 1}px`,
                  left: "calc(50vw - 470px)", width: "940px",
                  paddingTop: "10px",
                  background: "rgba(8,14,16,0.97)",
                  backdropFilter: "blur(28px)",
                  border: "1px solid rgba(87,215,226,0.13)",
                  borderRadius: "20px", overflow: "hidden",
                  boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
                  zIndex: 200,
                  opacity: resourcesOpen ? 1 : 0,
                  transform: resourcesOpen ? "translateY(0)" : "translateY(-8px)",
                  visibility: resourcesOpen ? "visible" : "hidden",
                  pointerEvents: resourcesOpen ? "auto" : "none",
                  transition: "opacity 0.18s ease-out, transform 0.18s ease-out",
                }}
              >
                <div style={{ display: "flex" }}>
                  {/* Left: 3-column grid for Tools / Research / Learn */}
                  <div style={{ flex: 1, padding: "28px 32px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px" }}>
                    {RESOURCES_SECTIONS.map((section) => (
                      <div key={section.label}>
                        <Link href={section.href} onClick={closeResourcesNow} style={{
                          display: "flex", alignItems: "center", gap: "6px",
                          color: "#57d7e2", fontSize: "11px", fontWeight: 700, fontFamily: ff,
                          letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "10px",
                          paddingLeft: "12px", textDecoration: "none",
                        }}>
                          {section.label} <ArrowUpRight />
                        </Link>
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          {section.items.map((item) => (
                            <ServiceItem key={item.label} label={item.label} desc={item.desc} href={item.href} onClose={closeResourcesNow} />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Right: Featured research post preview */}
                  <div style={{ width: "220px", flexShrink: 0, padding: "28px 24px", borderLeft: "1px solid rgba(255,255,255,0.06)" }}>
                    <div style={{ color: "#57d7e2", fontSize: "11px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "16px" }}>
                      Latest research
                    </div>
                    <Link href="/studies/ctr-study-2026" onClick={closeResourcesNow} style={{ textDecoration: "none", display: "block" }}>
                      <div style={{ borderRadius: "10px", overflow: "hidden", marginBottom: "12px", aspectRatio: "16/9", background: "linear-gradient(135deg, #0d2a2d 0%, #061214 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: "12px" }}>
                        <span style={{ color: "#57d7e2", fontSize: "28px", fontWeight: 800, fontFamily: ff, lineHeight: 1 }}>84%</span>
                      </div>
                      <span style={{ color: "rgba(87,215,226,0.8)", fontSize: "11px", fontWeight: 700, fontFamily: ff, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Original Research
                      </span>
                      <p style={{ color: "white", fontSize: "14px", fontWeight: 600, fontFamily: ff, lineHeight: 1.4, margin: "4px 0 0", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        The AI Overview Effect: How Google's AI Is Reshaping B2B Search
                      </p>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <a href={ctaHref} onClick={onCtaClick}
              style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                padding: "10px 22px",
                background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                borderRadius: "88px", border: "1px solid #57d7e2",
                boxShadow: "0px 4px 20px rgba(87,215,226,0.25)",
                textDecoration: "none",
                color: "#0a0c0c", fontSize: "14px", fontWeight: 700, fontFamily: ff,
                transition: "opacity 0.2s, transform 0.2s",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
            >
              {ctaLabel}
            </a>
          </div>
        </div>

        {/* ── MOBILE NAV ── */}
        <div ref={mobileRowRef} style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "16px 20px",
        }} className="nav-mobile">
          {/* Logo */}
          <Link href="/" style={{ display: "inline-block" }}>
            <img src={INDEXED_WORDMARK} alt="Indexed" width="110" height="18" style={{ width: "110px", height: "18px", objectFit: "contain" }} />
          </Link>

          {/* Hamburger — CSS animation instead of framer-motion */}
          <button type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "8px", display: "flex", flexDirection: "column",
              gap: "5px", alignItems: "center", justifyContent: "center",
            }}
          >
            <span style={{
              display: "block", width: "22px", height: "2px", background: "white", borderRadius: "2px",
              transformOrigin: "center",
              transform: mobileOpen ? "rotate(45deg) translateY(7px)" : "rotate(0deg) translateY(0)",
              transition: "transform 0.2s",
            }} />
            <span style={{
              display: "block", width: "22px", height: "2px", background: "white", borderRadius: "2px",
              opacity: mobileOpen ? 0 : 1,
              transition: "opacity 0.15s",
            }} />
            <span style={{
              display: "block", width: "22px", height: "2px", background: "white", borderRadius: "2px",
              transformOrigin: "center",
              transform: mobileOpen ? "rotate(-45deg) translateY(-7px)" : "rotate(0deg) translateY(0)",
              transition: "transform 0.2s",
            }} />
          </button>
        </div>

        {/* ── MOBILE DRAWER — CSS max-height transition ── */}
        <div style={{
          background: "#0a0c0c",
          borderTop: mobileOpen ? "1px solid rgba(87,215,226,0.1)" : "none",
          overflowY: mobileOpen ? "auto" : "hidden",
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
          maxHeight: mobileOpen ? `calc(100dvh - ${58 + navTop}px)` : "0",
          transition: "max-height 0.25s ease-in-out",
        }}>
          <div style={{ padding: "16px 20px 32px", display: "flex", flexDirection: "column", gap: "4px" }}>
            {/* Services accordion */}
            <button type="button"
              onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "14px 0",
                background: "none", border: "none", cursor: "pointer",
                color: "white", fontSize: "18px", fontWeight: 600, fontFamily: ff,
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                textAlign: "left",
              }}
            >
              Services
              <svg width="16" height="16" viewBox="0 0 12 12" fill="none"
                style={{ transform: mobileServicesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}>
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Services sub-menu — CSS max-height transition */}
            <div style={{
              overflow: "hidden",
              maxHeight: mobileServicesOpen ? "500px" : "0",
              transition: "max-height 0.2s ease-in-out",
            }}>
              <div style={{ padding: "8px 0 8px 12px", display: "flex", flexDirection: "column", gap: "2px" }}>
                <Link href="/seo-agency" onClick={closeMobile}
                  style={{ color: "#57d7e2", fontSize: "13px", fontWeight: 700, fontFamily: ff, textDecoration: "none", padding: "6px 0", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                  SEO Services
                </Link>
                {SEO_SERVICES.map(s => (
                  <Link key={s.label} href={s.href} onClick={closeMobile}
                    style={{ color: "rgba(255,255,255,0.8)", fontSize: "16px", fontWeight: 500, fontFamily: ff, textDecoration: "none", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    {s.label}
                  </Link>
                ))}
                <Link href="/seo-agency" onClick={closeMobile}
                  style={{ color: "#57d7e2", fontSize: "13px", fontWeight: 700, fontFamily: ff, textDecoration: "none", padding: "10px 0 6px", letterSpacing: "0.05em", textTransform: "uppercase", marginTop: "6px" }}>
                  Web Services
                </Link>
                {WEB_SERVICES.map(s => (
                  <Link key={s.label} href={s.href} onClick={closeMobile}
                    style={{ color: "rgba(255,255,255,0.8)", fontSize: "16px", fontWeight: 500, fontFamily: ff, textDecoration: "none", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    {s.label}
                  </Link>
                ))}
                <Link href="/services" onClick={closeMobile}
                  style={{ color: "#57d7e2", fontSize: "14px", fontWeight: 600, fontFamily: ff, textDecoration: "none", padding: "14px 0 6px", display: "block" }}>
                  View all services →
                </Link>
              </div>
            </div>

            {[
              { label: "Method", href: "/method" },
              { label: "Work", href: "/client-stories" },
            ].map(item => (
              <Link key={item.label} href={item.href} onClick={closeMobile}
                style={{
                  color: "white", fontSize: "18px", fontWeight: 600, fontFamily: ff,
                  textDecoration: "none", padding: "14px 0",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  display: "block",
                }}>
                {item.label}
              </Link>
            ))}

            {/* Resources accordion */}
            <button type="button"
              onClick={() => setMobileResourcesOpen(!mobileResourcesOpen)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                width: "100%", padding: "14px 0",
                background: "none", border: "none", cursor: "pointer",
                color: "white", fontSize: "18px", fontWeight: 600, fontFamily: ff,
                borderBottom: "1px solid rgba(255,255,255,0.06)",
                textAlign: "left",
              }}
            >
              Resources
              <svg width="16" height="16" viewBox="0 0 12 12" fill="none"
                style={{ transform: mobileResourcesOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s", flexShrink: 0 }}>
                <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <div style={{
              overflow: "hidden",
              maxHeight: mobileResourcesOpen ? "600px" : "0",
              transition: "max-height 0.2s ease-in-out",
            }}>
              <div style={{ padding: "8px 0 8px 12px", display: "flex", flexDirection: "column", gap: "2px" }}>
                {RESOURCES_SECTIONS.map((section) => (
                  <React.Fragment key={section.label}>
                    <Link href={section.href} onClick={closeMobile} style={{ color: "#57d7e2", fontSize: "13px", fontWeight: 700, fontFamily: ff, textDecoration: "none", padding: "10px 0 6px", letterSpacing: "0.05em", textTransform: "uppercase", display: "block" }}>
                      {section.label} →
                    </Link>
                    {section.items.map(item => (
                      <Link key={item.label} href={item.href} onClick={closeMobile}
                        style={{ color: "rgba(255,255,255,0.8)", fontSize: "16px", fontWeight: 500, fontFamily: ff, textDecoration: "none", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "block" }}>
                        {item.label}
                      </Link>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Mobile CTA */}
            <a href={ctaHref} onClick={(e) => { onCtaClick(e); closeMobile(); }}
              style={{
                display: "block", textAlign: "center", marginTop: "20px",
                padding: "15px 24px",
                background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                borderRadius: "88px", border: "1px solid #57d7e2",
                color: "#121212", fontSize: "16px", fontWeight: 700,
                fontFamily: ff, textDecoration: "none",
              }}>
              {ctaLabel}
            </a>
          </div>
        </div>
      </nav>

      <style>{`
        .nav-desktop { display: grid !important; }
        .nav-mobile { display: none !important; }
        @media (max-width: 900px) {
          .nav-desktop { display: none !important; }
          .nav-mobile { display: flex !important; }
        }
        /* Desktop sees the long descriptor; mobile gets the punchier short hook. */
      `}</style>
    </>
  );
}
